import React, { useState, useRef } from 'react';
import { Flag, AlertCircle } from 'lucide-react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';

interface ReportButtonProps {
  treeId: string;
  treeName: string;
}

export default function ReportButton({ treeId, treeName }: ReportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Por favor, describe el motivo del reporte');
      return;
    }

    setIsSubmitting(true);
    try {
      // Verificar si ya se ha reportado recientemente
      const lastReport = localStorage.getItem(`last_report_${treeId}`);
      if (lastReport) {
        const timeDiff = Date.now() - parseInt(lastReport);
        if (timeDiff < 24 * 60 * 60 * 1000) { // 24 horas
          throw new Error('Ya has reportado este árbol recientemente');
        }
      }

      const reportRef = doc(db, 'reports', `${treeId}_${Date.now()}`);
      await setDoc(reportRef, {
        treeId,
        treeName,
        reason: reason.trim(),
        status: 'pending',
        createdAt: serverTimestamp()
      });

      localStorage.setItem(`last_report_${treeId}`, Date.now().toString());
      toast.success('Reporte enviado correctamente');
      setIsOpen(false);
      setReason('');
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast.error(error.message || 'Error al enviar el reporte');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setReason('');
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="text-gray-500 hover:text-red-500 transition-colors flex items-center text-sm"
      >
        <Flag className="h-4 w-4 mr-1" />
        Reportar
      </button>

      {isOpen && createPortal(
        <div 
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
          onClick={handleClose}
        >
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

            <div 
              ref={modalRef}
              className="relative bg-white rounded-lg max-w-md w-full p-6 shadow-xl transform transition-all"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start mb-4">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Reportar contenido inapropiado
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Por favor, describe por qué consideras que este contenido es inapropiado.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Describe el motivo del reporte..."
                  required
                  maxLength={1000}
                  autoFocus
                />

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !reason.trim()}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar reporte'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}